import React, { useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { AppBar, Box, Button, ButtonBase, Drawer, IconButton, List, Toolbar } from "@mui/material";
import { ExitToApp, Menu as MenuIcon } from "@mui/icons-material";

import { useAuth } from "@context/AuthProvider";
import { useNotification } from "@context/NotificationProvider";
import { DASHBOARD_NODE, buildMenuTree } from "@/routes";
import { useSystemMenu } from "@hooks/index";
import { TabsProvider, useTabs } from "@context/TabsContext";
import { CurrentTabProvider } from "@context/CurrentTabContext";
import { TabBar } from "@components/layout/TabBar";
import { TabPageRenderer } from "@components/layout/TabPageRenderer";
import { MenuTreeItem } from "./MenuTreeItem";
import { styles } from "./Main.styles";

// MainContent é filho de TabsProvider e pode acessar useTabs().
const MainContent: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { setNotification } = useNotification();
  const { tabs, activeTabId, setActiveTab } = useTabs();
  const [drawerOpen, setDrawerOpen] = useState(true);
  const { data: systemMenu, isSuccess: isSystemMenuLoaded } = useSystemMenu();
  // `?.System?.Menus` (não só `?.System.Menus`) — um usuário sem NENHUMA permissão liberada (ex. conta
  // com todos os acessos revogados) pode fazer o request 116 voltar sem a chave `System`, não só com
  // `Menus` vazio. Sem o `?.` extra aqui, isso derrubava o app inteiro (TypeError não tratado, tela em
  // branco) em vez de acionar o aviso/logout abaixo.
  const menu = useMemo(() => [DASHBOARD_NODE, ...buildMenuTree(systemMenu?.System?.Menus ?? [])], [systemMenu]);

  // Caso esdrúxulo mas real: usuário autenticado sem NENHUMA permissão de tela (todas revogadas depois
  // do login, ou conta mal configurada) — a árvore do request 116 vem vazia/sem `System`. Em vez de
  // deixar o app quebrar ou mostrar só o Dashboard sem explicação, avisa "acesso negado" e desloga pelo
  // mesmo mecanismo já usado para sessão expirada/erro de autenticação (AuthProvider reage a
  // `notification.isAuthError`, fonte única de logout). O ref evita disparar mais de uma vez.
  const hasNotifiedEmptyMenuRef = useRef(false);
  useEffect(() => {
    if (!isSystemMenuLoaded || hasNotifiedEmptyMenuRef.current) return;
    if ((systemMenu?.System?.Menus?.length ?? 0) > 0) return;

    hasNotifiedEmptyMenuRef.current = true;
    setNotification({
      type: "error",
      message: "Acesso negado: seu usuário não possui nenhuma permissão liberada no sistema.",
      isAuthError: true
    });
  }, [isSystemMenuLoaded, systemMenu, setNotification]);

  const handleLogoClick = () => {
    setActiveTab(null);
    navigate("/inicio");
  };

  return (
    <Box sx={styles.root}>
      <AppBar position="fixed" sx={styles.appBar}>
        <Toolbar sx={styles.toolbar}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setDrawerOpen((prev) => !prev)}
            sx={styles.menuButton}>
            <MenuIcon />
          </IconButton>
          <ButtonBase sx={styles.logoWrapper} onClick={handleLogoClick} aria-label="Ir para o Início">
            <Box component="img" src={`${import.meta.env.BASE_URL}assets/Group.svg`} alt="Unicafé" sx={styles.logo} />
          </ButtonBase>
          <Button color="inherit" startIcon={<ExitToApp />} onClick={logout} sx={styles.sairButton}>
            Sair
          </Button>
        </Toolbar>
      </AppBar>

      <Drawer variant="persistent" open={drawerOpen} sx={styles.drawer(drawerOpen)}>
        <List>
          {menu.map((node) => (
            <MenuTreeItem key={node.label} node={node} depth={0} />
          ))}
        </List>
      </Drawer>

      <Box component="main" sx={styles.content}>
        {/* TabBar sempre no topo quando há tabs abertas */}
        {tabs.length > 0 && <TabBar />}

        {/* Dashboard e outras rotas normais — visível quando nenhuma tab está ativa */}
        <Box sx={styles.contentPanelVisible(activeTabId === null)}>
          <Outlet />
        </Box>

        {/* Painéis de tabs — todos montados, só o ativo é visível */}
        {tabs.map((tab) => (
          <Box key={tab.id} sx={styles.contentPanelVisible(tab.id === activeTabId)}>
            <CurrentTabProvider value={tab}>
              <TabPageRenderer path={tab.path} />
            </CurrentTabProvider>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// Main envolve MainContent com TabsProvider.
// TabsProvider usa useNavigate, que requer estar dentro do router — garantido
// porque Main é renderizado como elemento de uma rota protegida.
const Main: React.FC = () => (
  <TabsProvider>
    <MainContent />
  </TabsProvider>
);

export default Main;
